import { useSuspenseQueries } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';

import { apiQuery } from '../../state';
import { useSpacePrimarySite } from '../Organization';
import { getSiteURLForContent, type SitePreviewContent } from './getSiteURLForContent';

/**
 * Compute the URL to publicly preview a specific content in a space.
 * If the space can't be previewed, it returns null.
 */
export function useSiteContentPreviewURL(
    content: Omit<SitePreviewContent, 'siteSpaceId'> & {
        space: api.Space;
    }
) {
    const { space } = content;

    const [{ data: organizationContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationAllContent', {
                organizationId: space.organization,
            }),
        ],
    });
    const siteLookup = useSpacePrimarySite(organizationContent, space.id);

    if (!siteLookup) {
        return null;
    }

    const { site, siteSpace } = siteLookup;
    const rootURL = siteSpace?.urls.published ?? site.urls.published;
    if (!rootURL) {
        return null;
    }

    return getSiteURLForContent(rootURL, {
        siteSpaceId: siteSpace.id,
        changeRequestNumber: content.changeRequestNumber,
        revisionId: content.revisionId,
        pagePath: content.pagePath,
    }).toString();
}
